import { useEffect, useMemo, useState } from "react";
import {
  FieldHelperText,
  LabelWithIcon,
  PropertyFieldBinding,
  getIconForProperty,
  type FieldProps,
  type MapProperty,
  type PropertyOrBuilder,
  type ResolvedProperty,
  type StringProperty,
} from "@firecms/core";

import {
  DEFAULT_LOCALE,
  LOCALE_ORDER,
  LocaleName,
  type Locale,
  type LocalizedString,
} from "./constants";
import { formatFieldError } from "./formatFieldError";
import { hasLocalizedStringValue } from "./resolveLocalizedString";

/** Config string original pasada a `buildLocalizedStringProperty`. */
export type LocalizedStringFieldCustomProps = StringProperty;

export function LocalizedStringField({
  propertyKey,
  property,
  context,
  value,
  includeDescription,
  showError,
  error,
  disabled,
}: FieldProps<LocalizedString, LocalizedStringFieldCustomProps>) {
  const [activeLocale, setActiveLocale] = useState<Locale>(DEFAULT_LOCALE);

  const mapProperty = property as MapProperty<LocalizedString>;
  const localeProperties = (mapProperty.properties ?? {}) as Partial<
    Record<Locale, PropertyOrBuilder<string> | ResolvedProperty<string>>
  >;

  const activeLocaleProperty = useMemo(() => {
    return localeProperties[activeLocale];
  }, [localeProperties, activeLocale]);

  if (!activeLocaleProperty) {
    return null;
  }

  const isRequired = property.validation?.required === true;
  const requiredMessage =
    property.validation?.requiredMessage ??
    "At least one translation is required";

  useEffect(() => {
    if (!isRequired) {
      return;
    }

    const invalid = !hasLocalizedStringValue(value);
    if (invalid && showError) {
      context.formex.setFieldError(propertyKey, requiredMessage);
    } else if (!invalid && context.formex.errors[propertyKey] === requiredMessage) {
      context.formex.setFieldError(propertyKey, undefined);
    }
  }, [
    context.formex,
    context.formex.errors,
    isRequired,
    propertyKey,
    requiredMessage,
    showError,
    value,
  ]);

  const displayError =
    formatFieldError(error) ??
    (isRequired && showError && !hasLocalizedStringValue(value)
      ? requiredMessage
      : undefined);

  return (
    <div className="flex w-full flex-col gap-2">
      <LabelWithIcon
        icon={getIconForProperty(property, "small")}
        title={property.name}
        required={isRequired}
      />

      <div
        className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-slate-700"
        role="tablist"
      >
        {LOCALE_ORDER.map((locale) => {
          const isActive = locale === activeLocale;

          return (
            <button
              key={locale}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={disabled}
              onClick={() => setActiveLocale(locale)}
              className={[
                "px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "border-b-2 border-primary text-primary"
                  : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
                disabled ? "cursor-not-allowed opacity-50" : "",
              ].join(" ")}
            >
              {LocaleName[locale]}
            </button>
          );
        })}
      </div>

      <PropertyFieldBinding<string>
        propertyKey={`${propertyKey}.${activeLocale}`}
        property={activeLocaleProperty}
        context={context}
        includeDescription={false}
        disabled={disabled}
        minimalistView
      />

      <FieldHelperText
        includeDescription={includeDescription}
        showError={showError && Boolean(displayError)}
        error={displayError}
        disabled={disabled}
        property={property}
      />
    </div>
  );
}
